import { useCallback, useEffect, useMemo } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Modal, Skeleton } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import type { SymbolKey } from '@/entities/symbol';
import {
  CatalogTable,
  FilterProvider,
  useCatalog,
  useForwardCatalog,
  useSpotCatalog
} from '@/features/catalog';
import { useAppForm } from '@/features/dashboard/components/AppForm/AppForm/context';
import type { TabSymbol } from '@/features/dashboard/components/AppForm/AppForm/context/types';
import { useAddToAppContext } from '@/features/dashboard/state/AppContext';
import { AppType } from '@/features/dashboard/types';
import { FactsheetDialogButton } from '@/features/factsheet';
import { clearSelection, setIsDashboardAppSelector } from '@/features/symbol-selector';
import generateSkeletonKeys from '@/shared/lib/format/generateSkeletonKeys';
import getAmountLabel from '@/shared/lib/format/getAmountLabel';
import Typography from '@/shared/ui/typography/Text';
import { useAppDispatch } from '@/store/hooks';

const skeletonKeys = generateSkeletonKeys(30);

const CatalogModalBase = ({
  addItemsToBacklog,
  setOpen
}: {
  addItemsToBacklog: (items: TabSymbol[]) => void;
  setOpen: (open: boolean) => void;
}) => {
  const { form } = useAppForm();
  const type = form.state.values?.type;
  const isForwardCurves = useMemo(() => type === AppType.FWD, [type]);
  const {
    filterOpen,
    setFilterOpen,
    displaySettingsOpen,
    setDisplaySettingsOpen,
    table,
    expandedData,
    setExpandedData,
    forwardPeriodsInline,
    setForwardPeriodsInline,
    periodGroups,
    showDiscontinued,
    setShowDiscontinued,
    status
  } = useCatalog(isForwardCurves);

  const { data: spotCatalog } = useSpotCatalog();
  const { data: forwardCatalog } = useForwardCatalog();
  const catalog = isForwardCurves ? forwardCatalog : spotCatalog;

  const dispatch = useAppDispatch();

  // Enable special selector behavior only when Dashboard type is FWD
  useEffect(() => {
    dispatch(setIsDashboardAppSelector(true));
    return () => {
      dispatch(setIsDashboardAppSelector(false));
    };
  }, [dispatch]);

  const { setOnSubmit } = useAddToAppContext();

  const onSubmit = useCallback(
    (selectedSymbols: SymbolKey[]): void => {
      if (!selectedSymbols.length) {
        return;
      }
      const sourceSymbols = isForwardCurves
        ? Object.values(
            Object.groupBy(selectedSymbols, s => `${s.Code}|${s.PeriodType}|${s.TimeRef}`)
          ).flatMap(group => (group?.[0] ? [group[0]] : []))
        : selectedSymbols;

      const symbolsToAdd: TabSymbol[] = sourceSymbols
        .map(symbol => {
          const spot = catalog?.find(item => {
            const baseMatch =
              item.Symbol.Code === symbol.Code &&
              item.Symbol.PeriodType === symbol.PeriodType &&
              item.Symbol.TimeRef === symbol.TimeRef;
            if (!baseMatch) return false;
            if (isForwardCurves) return true; // ignore Period for FWD
            return item.Symbol.Period === symbol.Period;
          });

          if (!spot) return null;
          return {
            shortTitle: spot.Title || '',
            symbol,
            group: 'Prod_Indexes'
          };
        })
        .filter(Boolean) as TabSymbol[];

      addItemsToBacklog(symbolsToAdd);
      setOpen(false);
      dispatch(clearSelection());
    },
    [addItemsToBacklog, dispatch, isForwardCurves, setOpen, catalog]
  );

  const catalogLabel = useMemo(() => {
    if (isForwardCurves) return 'curve';
    return expandedData ? 'item' : 'code';
  }, [expandedData, isForwardCurves]);

  // Stable identity so it never breaks the memoized catalog rows (mirrors CatalogPage).
  const renderFactsheetButton = useCallback(
    (code: string, mode: 'modal' | 'newTab') => (
      <FactsheetDialogButton
        code={code}
        mode={mode}
      />
    ),
    []
  );

  useEffect(() => {
    setOnSubmit(() => (selectedSymbols: SymbolKey[]) => {
      onSubmit(selectedSymbols);
    });
    return () => {
      setOnSubmit(undefined);
    };
  }, [setOnSubmit, onSubmit]);

  const handleClose = useCallback(() => {
    setOpen(false);
    dispatch(clearSelection());
  }, [dispatch, setOpen]);

  return (
    <Modal
      open
      slotProps={{
        backdrop: {
          sx: {
            background: 'rgba(18, 22, 63, 0.80)'
          }
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: Z_INDEX.priorityLow
      }}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: '90%',
          height: '90vh',
          backgroundColor: 'gx.mainBackground',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            mt: 1,
            flexShrink: 0
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-end',
              gap: 2
            }}
          >
            <Typography
              size={32}
              sx={{
                fontWeight: 700,
                lineHeight: 1
              }}
            >
              {isForwardCurves ? 'Forward curves selector' : 'Spot Selector'}
            </Typography>
            <Typography
              size={20}
              sx={{ lineHeight: 1.1 }}
            >
              {getAmountLabel(table.getFilteredRowModel().rows.length, catalogLabel)}
            </Typography>
          </Box>
          <Box
            aria-label='close selector'
            role='button'
            tabIndex={0}
            sx={{
              position: 'relative',
              bottom: '8px',
              cursor: 'pointer'
            }}
            onClick={handleClose}
          >
            <CloseIcon
              sx={{
                width: 32,
                height: 32
              }}
            />
          </Box>
        </Box>
        {status === 'loading' ? (
          <Box
            sx={{
              overflow: 'auto',
              flex: 1,
              minHeight: 0
            }}
          >
            {skeletonKeys.map(key => (
              <Skeleton
                key={key}
                height='23px'
                sx={{ my: 2 }}
                width='100%'
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CatalogTable
              displaySettingsOpen={displaySettingsOpen}
              expandedData={expandedData}
              filterOpen={filterOpen}
              forwardPeriodsInline={forwardPeriodsInline}
              isForwardCurves={isForwardCurves}
              periodGroups={periodGroups}
              renderFactsheetButton={renderFactsheetButton}
              setDisplaySettingsOpen={setDisplaySettingsOpen}
              setExpandedData={setExpandedData}
              setFilterOpen={setFilterOpen}
              setForwardPeriodsInline={setForwardPeriodsInline}
              setShowDiscontinued={setShowDiscontinued}
              showDiscontinued={showDiscontinued}
              table={table}
            />
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export const CatalogModal = ({
  addItemsToBacklog,
  setOpen
}: {
  addItemsToBacklog: (items: TabSymbol[]) => void;
  setOpen: (open: boolean) => void;
}) => {
  return (
    <FilterProvider disableSearchParams>
      <CatalogModalBase
        addItemsToBacklog={addItemsToBacklog}
        setOpen={setOpen}
      />
    </FilterProvider>
  );
};
